import React, { useEffect, useState, useRef } from 'react';
import {
  Modal,
  Backdrop,
  Box,
  Typography,
  LinearProgress,
  Button,
  useTheme,
  Alert,
  useMediaQuery,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { useApiMutation, useApiQuery } from '@/hooks/useApi';
import { colors } from '../../theme/colors';
import { IconButton } from '../muiComponents';
import XLogo from '../../assets/images/logo/trx-favicon-dark.svg?react';
import { useProfile } from '@/hooks/useProfile';
import { useTranslation } from 'react-i18next';
import { useQueryClient } from '@tanstack/react-query';
// import { UserContext } from '@/providers/userContextProvider';
import ConfirmModal from './confirmModal';

const REFETCH_INTERVAL = 5000; //refetch interval in ms (5sec)

interface LoadingModalProps {
  userName?: string;
}

const LoadModal: React.FC<LoadingModalProps> = ({ userName }) => {
  const { profile, profileStatus } = useProfile();
  const { t } = useTranslation('loadingModal');
  const theme = useTheme();
  const queryClient = useQueryClient();

  // Responsive breakpoints
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isTablet = useMediaQuery(theme.breakpoints.between('sm', 'lg'));
  // const isDesktop = useMediaQuery(theme.breakpoints.up('lg'));

  // Get sidebar state from context
  // const { closed } = useContext(UserContext);

  const [completed, setCompleted] = useState(false);
  const [dismissed, setDismissed] = useState(false);
  const [showLoadingOverlay, setShowLoadingOverlay] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [wasLoadModalShown, setWasLoadModalShown] = useState(false);
  const lastUserIdRef = useRef<string | null>(null);
  const hasCalledApiForCurrentUserRef = useRef(false);

  type LoginDetailsData = {
    data: {
      userId: string;
      isUserDataLoaded: boolean;
      lastLoggedInAt: string;
    };
  };

  const { data, isLoading } = useApiQuery<LoginDetailsData>(
    ['/profile/loginDetails', profile?.value[0].eXpMember.Uuid as string],
    '/profile/loginDetails',
    {
      enabled: !!profile?.value[0].eXpMember.Uuid,
      refetchInterval: (query): number | false => {
        // Only refetching if isUserDataLoaded is false (first login scenario)
        const queryData = query.state.data as LoginDetailsData | undefined;
        return queryData?.data.isUserDataLoaded === false
          ? REFETCH_INTERVAL
          : false;
      },
      refetchIntervalInBackground: true,
      refetchOnWindowFocus: false,
    },
    false,
    {
      userId: profile?.value[0].eXpMember.Uuid as string,
    },
  );

  useEffect(() => {
    if (data?.data.isUserDataLoaded) {
      setCompleted(true);
    }
  }, [data?.data.isUserDataLoaded]);

  const { mutate } = useApiMutation<
    unknown,
    { userId: string; isUserDataLoaded: boolean; isFirstLogin: boolean }
  >({
    url: '/profile/updateLoginDetails',
    method: 'put',
    endpoint: 'chat',
  });

  const handleDismiss = () => {
    setShowLoadingOverlay(true);
    setDismissed(true);
  };

  // Reset API call tracking when component mounts or userId changed (new login)
  const currentUserId = profile?.value[0]?.eXpMember?.Uuid;
  useEffect(() => {
    if (currentUserId) {
      if (lastUserIdRef.current !== currentUserId) {
        hasCalledApiForCurrentUserRef.current = false;
        lastUserIdRef.current = currentUserId;
      }
    } else {
      lastUserIdRef.current = null;
      hasCalledApiForCurrentUserRef.current = false;
    }
  }, [currentUserId]);

  // Always call updateLoginDetails API on every login to update lastLoggedInAt
  const userId = profile?.value[0]?.eXpMember?.Uuid;
  useEffect(() => {
    if (!data || !userId) {
      return;
    }
    if (hasCalledApiForCurrentUserRef.current) {
      return;
    }

    const isFirstLogin = data.data.isUserDataLoaded === false;

    mutate(
      {
        userId: userId as string,
        isUserDataLoaded: true,
        isFirstLogin: isFirstLogin,
      },
      {
        onSuccess: () => {
          hasCalledApiForCurrentUserRef.current = true;
          queryClient.invalidateQueries({
            queryKey: ['/profile/loginDetails'],
          });
        },
      },
    );
  }, [data, userId, mutate, queryClient]);

  // Handle modal display logic - only show for first login
  const isUserDataLoaded = data?.data.isUserDataLoaded;
  useEffect(() => {
    if (!data) return;

    if (isUserDataLoaded === false) {
      // First login - modal will be shown
      setWasLoadModalShown(true);
    } else if (isUserDataLoaded === true) {
      // Subsequent login - don't show modal
      setCompleted(true);
      setShowLoadingOverlay(false);

      // Only showing ConfirmModal if LoadModal was actually shown to the user
      if (wasLoadModalShown) {
        setShowConfirmModal(true);
      }
    }
  }, [data, isUserDataLoaded, wasLoadModalShown]);

  if (profileStatus !== 'COMPLETED' || isLoading) return null;

  // Responsive styles
  const getModalStyles = () => ({
    backgroundColor: '#fff',
    borderRadius: isMobile ? 2 : 3,
    boxShadow: 24,
    p: isMobile ? 2 : 4,
    width: isMobile ? '95%' : isTablet ? 450 : 500,
    maxWidth: '95vw',
    textAlign: 'center' as const,
    position: 'relative' as const,
    maxHeight: isMobile ? '90vh' : '80vh',
    overflow: 'auto',
  });

  const getLogoContainerStyles = () => ({
    width: isMobile ? 40 : 50,
    height: isMobile ? 40 : 50,
    borderRadius: isMobile ? '8px' : '10px',
    mx: 'auto',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    mb: isMobile ? 1.5 : 2,
    boxShadow: '0 4px 12px rgba(0, 57, 209, 0.3)',
    background:
      'linear-gradient(135deg, #19469D 40%, rgba(25, 70, 157, 0.6) 100%)',
  });

  const getCloseButtonStyles = () => ({
    position: 'absolute' as const,
    top: isMobile ? 4 : 8,
    right: isMobile ? 4 : 8,
    color: 'grey.500',
    '&:hover': { color: 'grey.700' },
  });

  const getDismissButtonStyles = () => ({
    textTransform: 'none' as const,
    fontWeight: 'bold' as const,
    color: colors.white,
    borderRadius: isMobile ? '8px' : '10px',
    backgroundColor: '#19469D',
    py: isMobile ? 1 : 1.5,
    '&:hover': {
      backgroundColor: '#032363',
    },
  });

  const getProgressStyles = () => ({
    height: isMobile ? 6 : 8,
    borderRadius: 5,
    mb: isMobile ? 2 : 3,
    backgroundColor: '#eee',
    '& .MuiLinearProgress-bar': {
      background:
        'linear-gradient(to right,rgb(64, 104, 186), rgba(25, 70, 157, 1.0), rgba(25, 70, 157, 0.8), transparent)',
      borderRadius: 5,
    },
  });

  const getLoadingOverlayStyles = () => ({
    position: 'fixed' as const,
    top: isMobile ? 68 : 16,
    left: '50%',
    transform: 'translateX(-50%)',
    zIndex: theme.zIndex.appBar + 1,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    px: isMobile ? 1 : 2,
    width: '100%',
    maxWidth: isMobile ? '95vw' : 'auto',
  });

  const getTypographyStyles = (variant: 'h5' | 'body2' | 'caption') => {
    const baseStyles = {
      color:
        variant === 'h5'
          ? colors.primary
          : variant === 'body2'
            ? colors.darkRoyal10
            : colors.textGrey,
    };

    if (variant === 'h5') {
      return {
        ...baseStyles,
        fontSize: isMobile ? '1.25rem' : '1.5rem',
        fontWeight: 'bold',
        gutterBottom: true,
      };
    }

    if (variant === 'body2') {
      return {
        ...baseStyles,
        mb: isMobile ? 1 : 1.5,
        fontSize: isMobile ? '0.875rem' : '1rem',
      };
    }

    return {
      ...baseStyles,
      mb: isMobile ? 2 : 3,
      fontSize: isMobile ? '0.75rem' : '0.875rem',
    };
  };

  return (
    <>
      {!completed && !dismissed && (
        <Modal
          open
          data-testid="load-modal"
          sx={{
            zIndex: theme.zIndex.modal + 2,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            p: isMobile ? 1 : 2,
            mt: isMobile ? '60px' : 0,
          }}
        >
          <Backdrop
            open
            data-testid="load-modal-backdrop"
            sx={{
              zIndex: theme.zIndex.modal + 1,
              backgroundColor: 'rgba(0, 0, 0, 0.5)',
              top: isMobile ? '60px' : 0,
              height: isMobile ? 'calc(100vh - 60px)' : '100vh',
            }}
          >
            <Box sx={getModalStyles()} data-testid="load-modal-content">
              <IconButton
                onClick={handleDismiss}
                sx={getCloseButtonStyles()}
                aria-label="close"
                size={isMobile ? 'small' : 'medium'}
                data-testid="load-modal-close-button"
              >
                <CloseIcon fontSize={isMobile ? 'small' : 'medium'} />
              </IconButton>

              <Box
                sx={getLogoContainerStyles()}
                data-testid="load-modal-logo-container"
              >
                <XLogo
                  data-testid="load-modal-logo"
                  style={{
                    width: isMobile ? 20 : 24,
                    height: isMobile ? 20 : 24,
                  }}
                />
              </Box>

              <Typography
                variant="h5"
                sx={getTypographyStyles('h5')}
                data-testid="load-modal-welcome-text"
              >
                {t('welcome')} {userName}
              </Typography>

              <Typography
                variant="body2"
                sx={getTypographyStyles('body2')}
                data-testid="load-modal-loading-message"
              >
                {t('loadingMessage')}
              </Typography>

              <Typography
                variant="caption"
                sx={getTypographyStyles('caption')}
                data-testid="load-modal-subtext"
              >
                {t('subtext')}
              </Typography>

              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: 1,
                  mb: isMobile ? 0.5 : 1,
                }}
                data-testid="load-modal-status-container"
              >
                <Typography
                  variant="body2"
                  sx={{
                    color: colors.darkRoyal10,
                    fontSize: isMobile ? '0.875rem' : '1rem',
                  }}
                  data-testid="load-modal-loading-status"
                >
                  {t('loadingStatus')}
                </Typography>
              </Box>

              <LinearProgress
                variant="indeterminate"
                sx={getProgressStyles()}
                data-testid="load-modal-progress-bar"
              />

              <Button
                onClick={handleDismiss}
                variant="contained"
                fullWidth
                sx={getDismissButtonStyles()}
                data-testid="load-modal-dismiss-button"
              >
                {t('dismissButton')}
              </Button>
            </Box>
          </Backdrop>
        </Modal>
      )}

      {showLoadingOverlay && (
        <Box sx={getLoadingOverlayStyles()} data-testid="loading-overlay">
          <Alert
            severity="info"
            sx={{
              textAlign: 'center',
              fontSize: isMobile ? '0.875rem' : '1rem',
              py: isMobile ? 1 : 1.5,
            }}
            data-testid="loading-overlay-alert"
          >
            {t('loadingYourData')}
          </Alert>
        </Box>
      )}

      {showConfirmModal && (
        <ConfirmModal
          onClose={() => setShowConfirmModal(false)}
          data-testid="load-modal-confirm-modal"
        />
      )}
    </>
  );
};

export default LoadModal;
